import type { Directive, DirectiveBinding } from "vue";
import { storeToRefs } from "pinia";

interface CanBindingValue {
    permission?: string;
    anyPermissions?: string[];
    allPermissions?: string[];
    feature?: string;
    anyFeatures?: string[];
    allFeatures?: string[];
    dev?: boolean;
}

interface ElementWithComment extends HTMLElement {
    __vCanComment?: Comment;
}

function checkAuthorization(
    value: CanBindingValue,
    tokenStore: ReturnType<typeof useTokenStore>,
): boolean {
    const { payload } = storeToRefs(tokenStore);
    const config = useRuntimeConfig();

    const user = payload.value;
    if (!user) return false;

    // Check dev environment restriction
    if (config.public.appEnv !== "local" && value.dev) return false;

    // Check single permission
    if (value.permission) {
        if (!user.permissions.includes(value.permission)) return false;
    }

    // Check if has any of the permissions
    if (value.anyPermissions) {
        const hasAny = value.anyPermissions.some((permission) =>
            user.permissions.includes(permission),
        );
        if (!hasAny) return false;
    }

    // Check if has all of the permissions
    if (value.allPermissions) {
        const hasAll = value.allPermissions.every((permission) =>
            user.permissions.includes(permission),
        );
        if (!hasAll) return false;
    }

    // Check single feature
    if (value.feature) {
        if (user.features?.[value.feature] !== true) return false;
    }

    // Check if has any of the features
    if (value.anyFeatures) {
        const hasAny = value.anyFeatures.some(
            (feature) => user.features?.[feature] === true,
        );
        if (!hasAny) return false;
    }

    // Check if has all of the features
    if (value.allFeatures) {
        const hasAll = value.allFeatures.every(
            (feature) => user.features?.[feature] === true,
        );
        if (!hasAll) return false;
    }

    return true;
}

export const vCan: Directive = {
    mounted(
        el: HTMLElement,
        binding: DirectiveBinding<string | CanBindingValue>,
    ) {
        const element = el as ElementWithComment;
        const tokenStore = useTokenStore();

        // Handle simple string usage: v-can="'create_entry'"
        const value =
            typeof binding.value === "string"
                ? { permission: binding.value }
                : binding.value;

        const hasAccess = checkAuthorization(value, tokenStore);

        if (!hasAccess) {
            // Store a comment node as placeholder for potential updates
            const comment = document.createComment("v-can");
            element.parentNode?.insertBefore(comment, element);
            element.__vCanComment = comment;
            element.remove();
        }
    },

    updated(
        el: HTMLElement,
        binding: DirectiveBinding<string | CanBindingValue>,
    ) {
        const element = el as ElementWithComment;
        const tokenStore = useTokenStore();

        const value =
            typeof binding.value === "string"
                ? { permission: binding.value }
                : binding.value;

        const hasAccess = checkAuthorization(value, tokenStore);
        const comment = element.__vCanComment;

        if (!hasAccess && element.parentNode) {
            // Remove from DOM if it exists
            if (!comment) {
                const newComment = document.createComment("v-can");
                element.parentNode.insertBefore(newComment, element);
                element.__vCanComment = newComment;
            }
            element.remove();
        } else if (hasAccess && comment && comment.parentNode) {
            // Re-insert element if access was granted
            comment.parentNode.insertBefore(element, comment.nextSibling);
        }
    },

    unmounted(el: HTMLElement) {
        const element = el as ElementWithComment;
        // Clean up comment node
        const comment = element.__vCanComment;
        if (comment?.parentNode) {
            comment.remove();
        }
    },
};
